import clsx from 'clsx';
import type { ChangeEvent, KeyboardEvent, ReactNode } from 'react';
import { useEffect } from 'react';
import { createRef, useCallback, useState } from 'react';

import { Spinner } from '~components/generic/spinner/Spinner';
import { useSearchUsers } from '~services/api/hooks/user-management.hooks';
import styles from './UserSearch.module.css';

export type UserSearchProps = {
  disabled?: boolean;
  icon?: ReactNode;
  label?: string;
  placeholderText?: string;
  onSearchResult: (result: UserSearchResult) => void;
  autoFocus?: boolean;
};

export const UserSearch = ({
  onSearchResult,
  disabled = false,
  icon,
  label,
  placeholderText = '',
  autoFocus = false,
}: UserSearchProps) => {
  const refInput = createRef<HTMLInputElement>();
  const [searchInput, setSearchInput] = useState('');

  // Define: find user query
  const { data, refetch, isLoading } = useSearchUsers(searchInput);

  useEffect(() => {
    if (data === undefined) return;
    onSearchResult(data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // Handler: input change
  const handleInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (!disabled) {
        const newSearchInput = event.target.value || '';
        if (newSearchInput !== searchInput) {
          setSearchInput(newSearchInput);
        }
      }
    },
    [disabled, searchInput],
  );

  // Handler: listen to ENTER press on input field
  const handleInputKeyPress = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        if (refInput.current) {
          refInput.current.blur();
        }
        void refetch();
      }
    },
    [refInput, refetch],
  );

  return (
    <div className={clsx(styles.container, disabled && styles.containerDisabled)}>
      <label className={styles.label}>
        {Boolean(icon) && <span className={styles.labelIcon}>{icon}</span>}
        {Boolean(label) && <span className={styles.labelText}>{label}</span>}
      </label>
      <div className={styles.positioner}>
        <div className={styles.inputWrapper}>
          <input
            placeholder={placeholderText}
            value={searchInput}
            className={styles.input}
            disabled={disabled || isLoading}
            onKeyPress={handleInputKeyPress}
            onChange={handleInputChange}
            ref={refInput}
            autoFocus={autoFocus}
          />
        </div>
        {isLoading && (
          <div className={styles.processingIndicator}>
            <Spinner />
          </div>
        )}
      </div>
    </div>
  );
};
